import { useConversion } from '@/composables/core/useConversion'
import { captureConversionAttribution, convCookies, readLaunchAttribution } from '@/utils/conversionCookies'
import { convertEventName, isEventBlocked } from '@/utils/convertEventName'
import { resolveConversionDispatch } from '@/utils/eventContract'
import { createPwaOpenCapture, getDisplayMode, resolvePwaSrc, safeWindowStorage, scheduleIdle } from '@/utils/pwaLaunch'
import { storage } from '@/utils/storage'
import applyUmamiVendorRename from '@/utils/umamiVendorPayload'

export default defineNuxtPlugin((nuxtApp) => {
  const { $bridge, $gtag, $fb, $kwaiq, $tiktok, $umami } = nuxtApp
  const config = useRuntimeConfig()
  const isDev = config.public?.runningEnv === 'dev' || config.public?.runningEnv === 'qa'

  // 順序重要:先擷取啟動 URL 的 click 歸因,啟動快照才能反映「啟動當下」的 conv_* 與 token
  captureConversionAttribution(window.location.search)

  const launchAttribution = {
    ...readLaunchAttribution(),
    eventTime: Math.floor(Date.now() / 1000),
    hadTokenAtLaunch: !!useAuthToken().token.value,
  }

  const { sendConversionEvent } = useConversion()

  useRouter().afterEach((to) => {
    captureConversionAttribution(to.fullPath)
  })

  let pageTitle = ''

  function push(obj, eventID = null, opts = null) {
    const userInfoStore = nuxtApp.$pinia?._s.get('userInfo')
    const profile = userInfoStore?.userInfo || {}
    const user_properties = {
      level: profile.level || 0,
      strategy: profile.strategy || '',
      scope: profile.scope || '',
      topup: profile.topup || 0,
      verification_status: profile.verification_status || 0,
      uidSite: profile.uid_site || '',
      user_tag_RFM: profile.user_tag || '',
      agent_id: profile.agent_id || '',
    }
    const objData = { ...obj, user_id: user_properties.uidSite, user_properties }
    setPageTitle(objData)

    // 實施金額轉換與幣別代碼替換邏輯 (VNDK -> VND, IDRK -> IDR, 金額 * 1000)
    const isSpecialCurrency = ['VNDK', 'IDRK'].includes(objData.currency)
    const specialData = isSpecialCurrency
      ? {
          ...objData,
          value: objData.value ? Number(objData.value) * 1000 : objData.value,
          app_value: objData.app_value ? Number(objData.app_value) * 1000 : objData.app_value,
          currency: objData.currency.replace('K', ''),
        }
      : objData

    // Kwaiq 追蹤
    const kpix = storage.get('kpix')
    if ($kwaiq?.instance && kpix) {
      $kwaiq.instance(kpix)?.track(convertEventName(obj.event, 'kwai'), specialData)
    }

    // Google Analytics 追蹤
    if ($gtag) {
      $gtag('event', obj.event, specialData)
    }

    // FB Pixel 追蹤
    if ($fb) {
      let fbData = { ...specialData }
      const fbEventName = convertEventName(obj.event, 'facebook')

      // 只有當事件名稱有效時才發送 (過濾 null 或 undefined)
      if (fbEventName !== null && fbEventName !== undefined) {
        // [特定追蹤方法/特定事件] 當是註冊成功事件時 不需要傳遞幣別和金額
        if (fbEventName === 'CompleteRegistration') {
          const { currency: _currency, value: _value, ...restData } = fbData
          fbData = restData
        }
        $fb.query('trackCustom', fbEventName, fbData, eventID)
      }
    }

    // TikTok Pixel 追蹤
    if ($tiktok) {
      $tiktok.track(convertEventName(obj.event, 'tiktok'), specialData)
    }

    // Umami 追蹤
    if ($umami && $umami.enabled && !isEventBlocked(obj.event, 'umami')) {
      const umamiEvent = convertEventName(obj.event, 'umami')
      if (umamiEvent) {
        const { event: _evt, user_properties: _up, ...umamiPayload } = specialData
        // purchase / first_deposit 需走 Umami Revenue API（value → revenue，搭配 currency）。
        // 詳見 utils/umamiVendorPayload.js。
        $umami.track(umamiEvent, applyUmamiVendorRename(obj.event, umamiPayload, { isDev }))
      }
    }

    if ($bridge?.available) {
      $bridge.callHandler('pushEvents', objData, () => {
        console.log('[Bridge] Push Events')
      })
    }
    // 部分瀏覽器 / WebView 會注入存在但 postMessage 非函式的 window.jsBridge，
    // 僅用 ?. 仍會在呼叫時拋 TypeError，需以能力偵測確認可呼叫後再使用 (CLIENT-PWA-SSR-AG)
    if (typeof window.jsBridge?.postMessage === 'function') {
      window.jsBridge.postMessage(obj.event, JSON.stringify(objData || {}))
    }

    // S2S conversion 分支:任何例外不得影響上方既有通道
    try {
      const conversionDispatch = resolveConversionDispatch({
        event: obj.event,
        isDevLike: isDev,
        secondArg: eventID,
        opts,
        cookieTraceId: convCookies.get('conv_trace_id'),
      })
      if (conversionDispatch) {
        sendConversionEvent({
          event: obj.event,
          payload: specialData,
          eventId: conversionDispatch.eventId,
          attribution: conversionDispatch.attribution,
        })
      }
    } catch (error) {
      if (isDev) console.warn('[Conversion] dispatch failed:', error)
    }

    console.info(
      `%c[Analytics][DEFAULT: ${obj.event} | KWAI: ${convertEventName(obj.event, 'kwai')} | FB: ${convertEventName(obj.event, 'facebook')} | TIKTOK: ${convertEventName(obj.event, 'tiktok')}]`,
      'background-color: #fff000; color: #000000;'
    )
    // 重置pageTitle
    if (objData.event === 'close_page') {
      pageTitle = ''
    }
  }

  function setPageTitle(objData) {
    // 暫存pageTitle
    if (objData.page_title) {
      pageTitle = objData.page_title
    }

    // error_event 帶入暫存pageTitle
    if (objData.event === 'error_event') {
      objData.page_title = pageTitle ? pageTitle : document.title
    }
  }

  const pwaSrc = resolvePwaSrc({ search: window.location.search, displayMode: getDisplayMode() })
  if (pwaSrc) {
    scheduleIdle(
      createPwaOpenCapture({
        push,
        src: pwaSrc,
        launchAttribution,
        sessionStorage: safeWindowStorage(() => window.sessionStorage),
        localStorage: safeWindowStorage(() => window.localStorage),
      }),
      window
    )
  }

  const analytics = {
    push,
  }

  return {
    provide: {
      analytics,
    },
  }
})
